import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  FormControl,
  FormLabel,
  Textarea,
  Box,
  Alert,
  AlertDescription,
  AlertIcon,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { stagesList } from '@/constants/contact-stages';
import { useMemo, useState, useRef } from 'react';
import { SanitizedText } from '@/ui/SanitizedDom';
import { useGetContactRejectionReasonQuery } from '@/store/query/contacts';
import { useGetATSRejectionListQuery } from '@/store/query/ats';
import { Formik, Form, Field } from 'formik';
import { FormSelect } from '@/components/form/FormSelect';
import { FormSelect as DesignFormSelect } from '@/design-system/select';
import * as Yup from 'yup';
import {
  useMoveFlowStageMutation,
  useRejectFlowStageMutation,
} from '@/store/query/outreachCandidates';
import { useShowToast } from '@/hooks/useShowToast';
import { useKFlags } from '@/hooks/use-k-flags';
import { REJECT_REASON_GROUPS } from '@/constants/reject-reasons';
import { KText } from '@/ui-library/components';
import { XCircle } from '@kula-ai/icons-core';

const GroupHeading = (props) => (
  <KText size="sm-semi-bold" mb="6px" ml="4px">
    {props.children}
  </KText>
);

const groupOptions = (options) => {
  const groups = {};

  // Group options based on the "group" property
  options?.forEach((option) => {
    const groupName = option.group;
    if (!groups[groupName]) {
      groups[groupName] = [];
    }
    // Only add enabled and not deleted items
    if (option?.enabled && !option?.deleted && !option?.system_default)
      groups[groupName].push(option);
  });

  // Convert the grouped options into the desired format and filter out empty groups
  const groupedOptions = Object.entries(groups)
    .filter(([_, groupOptions]) => groupOptions.length > 0) // Filter out empty groups
    .map(([label, groupOptions]) => ({
      label: REJECT_REASON_GROUPS?.find((rejRsn) => rejRsn?.id === label)
        ?.label,
      options: groupOptions,
    }));

  return groupedOptions;
};

const stageRejectionSchema = Yup.object().shape({
  reasonId: Yup.object().required('Rejection reason is required'),
});

const getATSConfirmationText = ({ candidate, targetStage, jobName }) => {
  return `Are you sure you want to move <b>${
    candidate?.full_name || candidate?.first_name
  }</b> to the <b>${targetStage?.label}</b> stage in <b>${jobName}</b> job?`;
};

const getConfirmationText = ({ candidate, targetStage }) => {
  return `Are you sure to move <b>${
    candidate?.full_name || candidate?.first_name
  }</b>  to <b>${targetStage?.label}</b>?`;
};

export const RejectStage = ({
  candidate,
  onSaveSuccess = () => {},
  onClose = () => {},
  isOpen,
  outreachId,
  outreach = {},
}) => {
  const { ats_enabled } = useKFlags();
  const targetStage = stagesList.candidate_not_interested;
  const toast = useShowToast();
  const [error, setError] = useState(null);
  const [rejectFlowStage, { isLoading: isFlowStageLoading }] =
    useRejectFlowStageMutation();
  const { data, isLoading: isRejectionReasonLoading } =
    useGetContactRejectionReasonQuery(
      {
        contactId: candidate?.id,
      },
      { skip: ats_enabled }
    );
  const { data: rejectionList, isFetching: isRejectionLoading } =
    useGetATSRejectionListQuery({}, { skip: !ats_enabled });
  const formRef = useRef(null);

  const onRejectStage = async (values) => {
    try {
      const result = await rejectFlowStage({
        outreachId,
        candidateId: candidate?.id,
        stage: targetStage?.id,
        ...values,
        ats_enabled,
      });
      const updateStageError = result?.data?.[0]?.errors;
      // ATS Error
      if (updateStageError?.contact?.base?.length) {
        setError(updateStageError?.contact?.base[0]);
        return;
      }
      if (updateStageError?.length || updateStageError?.contact) {
        toast({
          title: 'Flow stage update failed',
          description: updateStageError[0],
          position: 'bottom-left',
          status: 'error',
          isClosable: true,
        });
        onClose();
        return;
      }
      toast({
        title: 'Flow stage updated',
        position: 'bottom-left',
        status: 'success',
        isClosable: true,
      });
      onClose();
      onSaveSuccess();
    } catch {
      // best-effort; ignore failures
    }
  };

  const validationSchema = useMemo(() => {
    let schema = stageRejectionSchema;
    if (!data?.rejection_reasons?.length) {
      schema = schema.omit(['reasonId']);
    }
    return schema;
  }, [data]);

  useEffect(() => {
    if (rejectionList?.length > 0) {
      formRef?.current?.setFieldValue(
        'reasonId',
        outreach?.not_interested_ats_rejection_reason
      );
    }
  }, [outreach?.not_interested_ats_rejection_reason, rejectionList]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          <Text size="lg-semi-bold">
            {ats_enabled ? 'Reason for rejection?' : 'Update Stage'}
          </Text>
          {ats_enabled ? (
            <Text size="sm" fontWeight="400" color="#464547">
              Please select a reason for rejecting this prospect
            </Text>
          ) : (
            <SanitizedText
              size="sm"
              fontWeight="400"
              variant="muted"
              html={
                ats_enabled && outreach?.ats_job?.name
                  ? getATSConfirmationText({
                      candidate,
                      targetStage: {
                        label: outreach?.interested_ats_job_stage?.name,
                      },
                      jobName: outreach?.ats_job?.name,
                    })
                  : getConfirmationText({ candidate, targetStage })
              }
            />
          )}
        </ModalHeader>
        <ModalBody>
          {error && (
            <Alert
              mb="12px"
              status="error"
              borderRadius="6px"
              bg="#FFF1F1"
              border="1px solid #DA1E28"
            >
              <AlertIcon as={XCircle} w="24px" h="24px" />
              <AlertDescription fontWeight="400" fontSize="14px">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {(!isRejectionReasonLoading || !isRejectionLoading) && (
            <Formik
              validateOnBlur={false}
              initialValues={{
                reasonId: '',
                note: '',
              }}
              validationSchema={validationSchema}
              onSubmit={onRejectStage}
              innerRef={formRef}
            >
              <Form noValidate>
                {(data?.rejection_reasons?.length > 0 ||
                  rejectionList?.length > 0) && (
                  <>
                    {!ats_enabled && (
                      <Alert
                        status="info"
                        borderRadius="8px"
                        mb="16px"
                        bg="#DEEBFF"
                      >
                        <AlertIcon alignSelf="normal" color="#0747A6" />
                        <Text>
                          The rejection reason will be synced with your ATS and
                          will be accessible from there as well.
                        </Text>
                      </Alert>
                    )}
                    <Field name="reasonId">
                      {({ form }) => (
                        <FormControl mb="12px" isRequired>
                          <FormLabel
                            fontWeight="semiBold"
                            fontSize="14px"
                            htmlFor="reasonId"
                          >
                            Rejection reasons
                          </FormLabel>

                          {rejectionList?.length > 0 ? (
                            <DesignFormSelect
                              id="reasonId"
                              options={groupOptions(rejectionList)}
                              placeholder="Select a reason"
                              isLoading={isRejectionLoading}
                              getOptionLabel={(rejection) => rejection.reason}
                              getOptionValue={(rejection) => rejection.id}
                              components={{
                                GroupHeading,
                              }}
                            />
                          ) : (
                            <FormSelect
                              placeholder="Select"
                              options={data?.rejection_reasons}
                              fieldName="reasonId"
                            />
                          )}

                          {form.errors.reasonId && (
                            <Box
                              color="#DA1E28"
                              fontSize="14px"
                              fontWeight="400px"
                              mt="6px"
                            >
                              {form.errors.reasonId}
                            </Box>
                          )}
                        </FormControl>
                      )}
                    </Field>
                  </>
                )}

                <Field name="note">
                  {({ field, form }) => (
                    <FormControl mb="12px">
                      <FormLabel
                        fontWeight="semiBold"
                        fontSize="14px"
                        htmlFor="note"
                      >
                        Add comments
                      </FormLabel>
                      <Textarea
                        {...field}
                        id="note"
                        name="note"
                        isInvalid={form.touched.note && form.errors.note}
                        placeholder="Write your comments here."
                      />
                      {form.touched.note && form.errors.note && (
                        <Box
                          color="#DA1E28"
                          fontSize="14px"
                          fontWeight="400px"
                          mt="6px"
                        >
                          {form.errors.note}
                        </Box>
                      )}
                    </FormControl>
                  )}
                </Field>
                <ModalFooter pr="0">
                  <Button variant="secondary" mr="8px" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    _hover={{ bg: '#EB4A27' }}
                    bg="#EB4A27"
                    isLoading={isFlowStageLoading}
                    type="submit"
                  >
                    Update
                  </Button>
                </ModalFooter>
              </Form>
            </Formik>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export const UpdateStage = ({
  candidate,
  onSaveSuccess,
  onClose,
  isOpen,
  outreachId,
  outreach = {},
}) => {
  const { ats_enabled } = useKFlags();
  const targetStage = stagesList.candidate_interested;
  const toast = useShowToast();
  const [moveFlowStage, { isLoading: isFlowStageLoading }] =
    useMoveFlowStageMutation();
  const onUpdateStage = async () => {
    try {
      const result = await moveFlowStage({
        candidateId: candidate?.id,
        outreachId,
        stage: targetStage?.id,
      });

      const updateStageError = result?.data?.data?.[0]?.errors;
      if (updateStageError?.length) {
        toast({
          title: 'Flow stage update failed',
          description: updateStageError[0],
          position: 'bottom-left',
          status: 'error',
          isClosable: true,
        });
        onClose();
        return;
      }
      toast({
        title: 'Flow stage updated',
        position: 'bottom-left',
        status: 'success',
        isClosable: true,
      });
      onClose();
      onSaveSuccess();
    } catch {
      // best-effort; ignore failures
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          <Text size="lg-semi-bold">Update Stage</Text>
        </ModalHeader>
        <ModalBody>
          <SanitizedText
            size="sm"
            variant="muted"
            html={
              ats_enabled && outreach?.ats_job?.name
                ? getATSConfirmationText({
                    candidate,
                    targetStage: {
                      label: outreach?.interested_ats_job_stage?.name,
                    },
                    jobName: outreach?.ats_job?.name,
                  })
                : getConfirmationText({ candidate, targetStage })
            }
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" mr="8px" onClick={onClose}>
            Cancel
          </Button>
          <Button isLoading={isFlowStageLoading} mr={3} onClick={onUpdateStage}>
            Confirm
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
